import {expectEither, UploadClient, useUploadClient} from '@raison/network-facade';
import {invariant} from '@raison/toolbox';
import {useMutation} from '@tanstack/react-query';
import {absurd} from 'fp-ts/lib/function';
import {array, string, type, TypeOf} from 'io-ts';

async function sendDocuments(
    url: string,
    body: FormData,
    {
        uploader,
        onProgress,
    }: {
        uploader: UploadClient;
        onProgress: (percent: number) => void;
    }
) {
    const response = await uploader(url, {body, onProgress}).then(
        ...expectEither({
            200: null,
            400: FilesValidationResult,
        })
    );
    return response;
}

type FilesValidationResult = TypeOf<typeof FilesValidationResult>;
const FilesValidationResult = type({
    fields_errors: array(
        type({
            message: string,
            property_path: string,
        })
    ),
});

export enum UploadResult {
    Abort,
    Success,
    Problem,
}

export function useDocsUploadMutation(uploadUrl: string) {
    const uploader = useUploadClient();

    return useMutation({
        gcTime: 0,
        async mutationFn({files, setProgress: onProgress}: {files: File[]; setProgress: (percent: number) => void}) {
            const formData = new FormData();
            for (const file of files) {
                formData.append('documents[]', file, file.name);
            }
            const response = await sendDocuments(uploadUrl, formData, {uploader, onProgress});
            if (response.status === 'Abort') {
                return {code: UploadResult.Abort as const};
            }
            if (response.status === 200) {
                return {code: UploadResult.Success as const, successAt: Date.now()};
            }
            if (response.status === 400) {
                const {file, message} = pickProblemFile(response.json, formData);
                return {code: UploadResult.Problem as const, message, fileNames: [file.name]};
            }
            return absurd<never>(response);
        },
    });
}

function pickProblemFile(json: FilesValidationResult, formData: FormData) {
    const {message, property_path: path} = json.fields_errors[0];
    const [_, indexString] = path.match(/^documents\[(\d+)]$/) ?? [];
    invariant(!!indexString, `property_path must match ^documents\\[(\\d+)]$, got ${path}`);

    const fileIndex = parseInt(indexString);
    const file = formData.getAll('documents[]')[fileIndex];
    invariant(file instanceof File, `Field documents[] must contain Files, got ${file}`);
    return {message, file};
}
